import gifFrames from "gif-frames";
import { dimensionCache } from "./caching/DimensionCache";

export const getImageSourceWithFallback = ({
  optimalSource,
  fallbackSource,
  callback
}) => {
  // try to get the optimal image
  cacheImage(optimalSource).then(
    () => {
      // if the optimal image can be cached then use it
      return callback(optimalSource);
    },
    () => {
      // if the optimal image can not be cached then use the original source as a fallback
      return callback(fallbackSource);
    }
  );
};

export const getGifUploadData = async file => {
  const imageUrl = window.URL.createObjectURL(file);
  const frameData = await gifFrames({ url: imageUrl, frames: "all" });
  let duration = 0;
  let frames = 0;
  frameData.forEach(frame => {
    // GIF frame delay is represented in 1/100 of a second. Multiplying this by 10 converts it to milliseconds
    duration += frame.frameInfo.delay * 10;
    frames++;
  });

  // When an uploaded gif has 0 frame delay it will cause the duration to return 0
  // Browsers play GIFs back at a default frame delay of 100ms if no frame delay is provided.
  if (duration === 0 && frames > 1) {
    duration = frames * 100;
  }

  return {
    duration
  };
};

export const getImageSizeFromSrc = async (src, timeout = 5000) => {
  const getImageSize = src => {
    return new Promise((resolve, reject) => {
      let img = new Image();
      img.onload = () => {
        // when an image has no intrinsic dimensions it is treated differently in each browser in terms of
        // base dimensions, and firefox for example will set width and height to 0 by default
        // we will set default for these to 150 to avoid having issues inserting these elements
        resolve({ height: img.height || 150, width: img.width || 150 });
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  const timeoutPromise = new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`Image dimension loading timed out after ${timeout}ms`));
    }, timeout);
  });

  try {
    return await Promise.race([getImageSize(src), timeoutPromise]);
  } catch (error) {
    console.warn(`Failed to load image dimensions from ${src}:`, error.message);
    // Return default dimensions on timeout or error
    return { height: 600, width: 800 };
  }
};

/**
 * Get image size from source with caching support
 * Checks cache first, then falls back to loading if not cached
 */
export const getImageSizeFromSrcWithCache = async (src, timeout = 5000) => {
  if (!src) {
    return { height: 150, width: 150 };
  }

  // Check cache first
  const cached = dimensionCache.getDimensions(src);
  if (cached) {
    return cached;
  }

  // Load dimensions and cache result
  const dimensions = await getImageSizeFromSrc(src, timeout);
  dimensionCache.setDimensions(src, dimensions);

  return dimensions;
};

/**
 *
 * @param {Object} imageElement an object representing the image element to get dimensions for
 * @returns {Object} imageDimensions - contains width and height dimensions for an image
 */
export const getImageDimensionsFromElement = async imageElement => {
  let imageDimensions;
  // check for dimensions in the media object first
  if (
    imageElement.media &&
    imageElement.media.width &&
    imageElement.media.height
  ) {
    imageDimensions = {
      width: imageElement.media.width,
      height: imageElement.media.height
    };
    // Cache media dimensions for future use
    const src =
      imageElement.url ||
      imageElement.src ||
      imageElement.originalUrl ||
      imageElement.originalSrc ||
      imageElement.previewUrl ||
      imageElement.previewSrc;
    if (src) {
      dimensionCache.setDimensions(src, imageDimensions);
    }
  } else {
    // fallback to using the main url, the original url and then finally the preview url
    // Use cached version for performance
    try {
      imageDimensions = await getImageSizeFromSrcWithCache(
        imageElement.url || imageElement.src
      );
    } catch {
      try {
        imageDimensions = await getImageSizeFromSrcWithCache(
          imageElement.originalUrl || imageElement.originalSrc
        );
      } catch {
        imageDimensions = await getImageSizeFromSrcWithCache(
          imageElement.previewUrl || imageElement.previewSrc
        );
      }
    }
  }
  return imageDimensions;
};

export const cacheImage = src => {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.src = src;
    image.onload = () => {
      resolve(true);
    };

    image.onerror = () => {
      reject(false);
    };
  });
};

export const isImageCached = src => {
  const image = new Image();
  image.src = src;

  return image.complete;
};
